<template>
  <div
    aria-label="Formulaire de création de manifeste d'échange"
    role="form"
  >
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
      aria-busy="true"
      aria-live="polite"
    >
      <b-spinner
        variant="primary"
        label="Chargement en cours..."
        aria-label="Chargement en cours..."
      />
    </div>

    <b-card>
      <b-form-group
        label="Fournisseur *"
        aria-label="Sélection du fournisseur logistique (obligatoire)"
        description="Sélectionnez le fournisseur concerné par le manifeste d'échange."
      >
        <validation-provider
          #default="{ errors }"
          name="Fournisseur"
        >
          <v-select
            v-model="supplier"
            :clearable="false"
            placeholder="Sélectionner un fournisseur logistique"
            label="username"
            :options="suppliers"
            aria-label="Liste déroulante des fournisseurs logistiques"
            aria-required="true"
            @input="getOrders"
          />
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>
      </b-form-group>

      <b-form-group
        label="Référence de colis *"
        label-for="colis-ref-input"
        aria-label="Saisir la référence du colis échange (obligatoire)"
        description="Saisissez ou scannez la référence du colis à ajouter au manifeste d'échange."
      >
        <b-input-group class="input-group-merge">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="MaximizeIcon"
              aria-hidden="true"
            />
          </b-input-group-prepend>
          <b-form-input
            id="colis-ref-input"
            ref="select"
            v-model="ref"
            placeholder="Saisir ou scanner la référence du colis"
            aria-label="Champ de saisie de la référence du colis échange"
            aria-required="true"
            @change="checkOrder"
          />
        </b-input-group>
      </b-form-group>
    </b-card>

    <div class="d-flex justify-content-between">
      <b-card>
        <h4>échange au depot : {{ orders.length }}</h4>
        <vue-good-table
          :columns="columns"
          :rows="processedOrders"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span>{{ getDisplayReference(props.row) }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
            </span>

            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`exchange-row-${props.row.id}-add-icon`"
                  icon="CheckSquareIcon"
                  class="cursor-pointer mr-1"
                  size="16"
                  color="green"
                  @click="addItem(props.row)"
                />
                <b-tooltip
                  title="Ajouter"
                  class="cursor-pointer"
                  :target="`exchange-row-${props.row.id}-add-icon`"
                />
              </span>
            </span>

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </b-card>

      <b-card class="ml-1">
        <h4>échange au fournisseur : {{ selected.length }}</h4>
        <vue-good-table
          :columns="columns"
          :rows="processedSelected"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm }"
        >
          <template
            slot="table-row"
            slot-scope="props"
          >
            <span v-if="props.column.field === 'id'">
              <span>{{ getDisplayReference(props.row) }}</span><br>
              <span
                v-if="props.row.is_an_exchange"
                style="color:red ; font-weight: bold;"
              > Echange </span>
            </span>

            <span v-else-if="props.column.field === 'action'">
              <span>
                <feather-icon
                  :id="`exchange-row-${props.row.id}-remove-icon`"
                  icon="DeleteIcon"
                  color="red"
                  class="cursor-pointer mr-1"
                  size="16"
                  @click="resetItem(props.row)"
                />
                <b-tooltip
                  title="Annuler"
                  class="cursor-pointer"
                  :target="`exchange-row-${props.row.id}-remove-icon`"
                />
              </span>
            </span>

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>

        <b-form-group class="d-flex justify-content-end">
          <span id="save-exchange-manifest-btn-wrapper">
            <b-button
              variant="gradient-primary"
              class="ml-1 d-inline-block mt-1"
              :class="{ 'disabled-cursor': isSaving || !isExchangeManifestValid }"
              :disabled="isSaving || !isExchangeManifestValid"
              @click="createManifest"
            >
              <b-spinner
                v-if="isSaving"
                small
                class="mr-1"
              />
              <feather-icon
                v-else
                icon="PlusIcon"
              />
              <span class="ml-1">Enregistrer</span>
            </b-button>
            <b-tooltip
              v-if="!isExchangeManifestValid"
              target="save-exchange-manifest-btn-wrapper"
              triggers="hover focus"
              placement="top"
            >
              <template v-if="!supplier || !supplier.id">
                Sélectionnez un fournisseur logistique pour activer l'enregistrement.
              </template>
              <template v-else-if="selected.length === 0">
                Ajoutez au moins un colis échange pour activer l'enregistrement.
              </template>
            </b-tooltip>
          </span>
        </b-form-group>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BTooltip,
  BSpinner,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ok from '@/assets/audio/ok.mp3'
import notexist from '@/assets/audio/notexist.mp3'

const EXCHANGE_STATUS = 25

export default {
  name: 'ExchangeManifestCreate',
  components: {
    BButton,
    ValidationProvider,
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BTooltip,
    BSpinner,
    vSelect,
    BCard,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      suppliers: [],
      supplier: '',
      columns: [
        {
          label: 'Action',
          field: 'action',
        },
        {
          label: 'BL',
          field: 'id',
          filterOptions: {
            enabled: true,
            placeholder: 'BL',
          },
        },
        {
          label: 'Status',
          field: 'status',
          filterOptions: {
            enabled: true,
            placeholder: 'Status',
          },
        },
        {
          label: 'Client',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Client',
          },
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Gouvernorat',
          },
        },
        {
          label: 'Motifs de retour',
          field: 'motifs_retour',
        },
      ],
      orders: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      isLoading: false,
      isSaving: false,
      ok,
      notexist,
    }
  },
  computed: {
    isExchangeManifestValid() {
      return (
        this.supplier
        && this.supplier.id
        && this.selected.length > 0
      )
    },
    processedOrders() {
      return this.orders.map(order => this.processOrderForDisplay(order))
    },
    processedSelected() {
      return this.selected.map(order => this.processOrderForDisplay(order))
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getSuppliers()
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.select) this.$refs.select.focus()
    })
  },
  methods: {
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async withTitle(text) {
      try {
        await this.$swal({
          title: 'Alerte',
          icon: 'warning',
          text,
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })
        this.ref = ''
      } catch (e) {
        this.ref = ''
      }
    },
    async checkOrder() {
      const searchRef = this.ref.trim()
      if (!searchRef) return

      if (!this.supplier || !this.supplier.id) {
        await this.withTitle("Veuillez sélectionner un fournisseur avant d'ajouter un colis.")
        await this.playAudio(this.notexist)
        return
      }

      const result = this.orders.find(order => orderMatchesReference(order, searchRef))

      if (result) {
        await this.addItem(result)
        this.ref = ''
        await this.playAudio(this.ok)
      } else {
        await this.withTitle('Colis non trouvé pour ce fournisseur ou ne possède pas le statut Échange.')
        await this.playAudio(this.notexist)
      }
    },
    async getOrders() {
      if (!this.supplier || !this.supplier.id) {
        this.orders = []
        this.selected = []
        return
      }

      this.isLoading = true
      try {
        const { data } = await axios.get('/api/orders/list/', {
          params: {
            supplier: this.supplier.id,
            status: EXCHANGE_STATUS,
            exclude_exchange_manifest_orders: true,
            page_size: 500,
          },
        })
        const orders = data.results || data
        this.orders = orders.filter(order => Number(order.status) === EXCHANGE_STATUS)
        this.selected = []
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Erreur lors du chargement des colis échange.'
        this.showToast('danger', 'top-center', errorMessage)
      } finally {
        this.isLoading = false
      }
    },
    async getSuppliers() {
      this.isLoading = true
      try {
        const { data } = await axios.get('/api/auth/users/suppliers-with-exchange-orders/')
        this.suppliers = data.results || data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des fournisseurs')
      } finally {
        this.isLoading = false
      }
    },
    async addItem(item) {
      const source = this.orders.find(order => String(order.id) === String(item.id)) || item
      if (!this.selected.find(order => String(order.id) === String(source.id))) {
        this.selected.push(source)
      }
      this.orders = this.orders.filter(order => String(order.id) !== String(source.id))
    },
    async resetItem(item) {
      const source = this.selected.find(order => String(order.id) === String(item.id)) || item
      if (!this.orders.find(order => String(order.id) === String(source.id))) {
        this.orders.push(source)
      }
      this.selected = this.selected.filter(order => String(order.id) !== String(source.id))
    },
    async createManifest() {
      if (!this.isExchangeManifestValid) return

      const orderIds = this.selected.map(order => order.id)
      const orderSummary = `<ul style="text-align:left">${this.selected.map(order => `<li><b>BL:</b> ${this.getDisplayReference(order)} | <b>Status:</b> Échange | <b>Client:</b> ${order.name || ''}</li>`).join('')}</ul>`

      const confirmed = await this.$swal({
        title: 'Confirmation',
        html: `<div>Êtes-vous sûr de vouloir créer ce manifeste d'échange ?<br><br><b>Commandes concernées :</b>${orderSummary}</div>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, créer',
        cancelButtonText: 'Annuler',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      if (!confirmed.isConfirmed) return

      this.isSaving = true
      try {
        const { data } = await axios.post('/api/exchange-manifests/create/', {
          order_ids: orderIds,
        })
        this.showToast('success', 'top-center', `Manifeste d'échange #${data.id} créé avec succès.`)
        this.$router.push({ name: 'exchange-manifest-detail', params: { id: data.id } })
      } catch (error) {
        const errorMessage = error.response?.data?.error || 'Erreur lors de la création du manifeste.'
        this.showToast('danger', 'top-center', errorMessage)
      } finally {
        this.isSaving = false
      }
    },
    getDisplayReference(order) {
      if (order.display_cross_tenant_barcode && order.cross_tenant_barcode) {
        return order.cross_tenant_barcode
      }
      return order.id
    },
    processOrderForDisplay(order) {
      return {
        ...order,
        status: Number(order.status) === EXCHANGE_STATUS ? 'Échange' : order.status,
        motifs_retour: '',
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
  },
}
</script>

<style lang="scss" >
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
